import { DndContext } from "@dnd-kit/core";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Box, Collapse, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { animate } from "framer-motion";
import { DateTime } from "luxon";
import { type Dispatch, type FC, type ReactNode, type SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../hooks/useRoleColors";
import { useValueEveryMinute } from "../../hooks/useValueEveryMinute";
import { dailyViewGapsTableSortingKey } from "../../services/LocalStorageService";
import { DAY_GAPS_POSTED } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { computeHppdBreakdown } from "../../utils/hppdBreakdown";
import {
	ChangesSource,
	HppdSeparationType,
	LocationMode,
	NotificationType,
	type Nurse,
	type NurseFullRole,
	type NurseInShift,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	StatsMode,
} from "../../utils/models";
import { getGroupNameForRoles } from "../../utils/roles";
import { buildNurseUnionMap } from "../../utils/utilFunctions";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { DayViewLocationFilter } from "../DayViewContent/DayViewLocationFitler";
import { useRemovalOffersReplacementSearch } from "../NormalizedNurseMenu/DeleteRecurringOrSingleShift/useRemovalOffersReplacementSearch";
import { NormalizedNurseMenu } from "../NormalizedNurseMenu/NormalizedNurseMenu";
import { MessageWorkingFilterEnum } from "../StaffCommunication/util/Broadcast/MessageFilter";
import { getShiftPickupMessage, NewBroadcastMessageModal } from "../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { StaffingChanges } from "../StaffingChanges/StaffingChanges";
import { buildJobRatios, buildRoleRatios, CensusGroupHeaderContent } from "../StaffTable/CensusGroupHeaderContent";
import { getAutoGroupSubtext } from "../StaffTable/utils";
import { ContractSectionsDropdown } from "../team/ContractSectionsDropdown";
import { holidayForShift } from "../team/Holidays/schedule/scheduleHolidays";
import { usePageScheduleHolidays } from "../team/Holidays/schedule/scheduleHolidaysContext";
import { RoleOrJobSelector } from "../UIToolkit/molecules/RoleOrJobSelector";
import { TimeFilterChips } from "../UIToolkit/molecules/TimeFilterChips";
import { SystemSortingButtonWithPopup } from "../UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { ClickableGap } from "./ClickableGap";
import { gapsCallOffAnchorItem } from "./callOffAnchor";
import { DAY_VIEW_GAPS_PADDING } from "./consts";
import { DailyRow, type RowElement } from "./DailyRow/DailyRow";
import { NurseInShiftMainText } from "./DailyRow/NurseInShiftMainText";
import { NurseInShiftTrailingNode } from "./DailyRow/NurseInShiftTrailingNode/NurseInShiftTrailingNode";
import { RequestsLeadingContent } from "./DailyRow/RequestsLeadingContent/RequestsLeadingContent";
import { PostedGap } from "./PostedGap";
import { PtoGapViewSection } from "./PtoGapViewSection";
import { useDayViewGapsState } from "./useDayViewGapsState";
import {
	colorForMissingBlock,
	colorForMissingBlockHighlighted,
	colorForPostedShiftBlock,
	colorForReplaceRequestBlock,
	type DayViewGap,
	defaultSortStateDailyGapView,
	fontColorMissingBlock,
	fontColorMissingBlockHighlighted,
	getGapSignatureKey,
	hourJumpBetweenMarkers,
	hourMarkerTypographySx,
	locationAndRolesWidth,
	markerColor,
	postedShiftString,
	replaceRequestString,
	rowGap,
	rowPadding,
	shiftGaps,
	sortingOptions,
} from "./utils";

const clampToDayStart = (time: DateTime, dayStart: DateTime): DateTime => (time < dayStart ? dayStart : time);

// We ease the scroll container's scrollTop ourselves (native smooth `scrollIntoView` isn't honored on
// this nested container — it jumps), then flash the posted-shift block once the scroll completes.
const POSTED_SCROLL_DURATION_S = 0.25;
const POSTED_FLASH_DURATION_S = 1.0;

// Functional / Categorical: encodes draft-mode change types (added/deleted/updated).
// Must stay distinct across user themes so changes are recognizable.
const draftAddedBgColor = "#1DD87133";
const draftDeletedBgColor = "#FDE5E5";
const draftUpdatedBgColor = "#CEF8FF";
const draftDeletedOpacity = 0.6;
const postShiftBlockDomIdLeading = "posted-shift-block";

type DraftVisuals = {
	bgColor?: string;
	icon?: ReactNode;
	strikeThrough?: boolean;
	opacity?: number;
};

const getDraftVisuals = (shift: NurseInShift): DraftVisuals => {
	if (shift.isPendingToDelete) {
		return { bgColor: draftDeletedBgColor, icon: <Delete sx={{ fontSize: 14 }} />, strikeThrough: true, opacity: draftDeletedOpacity };
	}
	if (shift.isPending) {
		return { bgColor: draftAddedBgColor, icon: <Add sx={{ fontSize: 14 }} /> };
	}
	if (shift.isPendingToUpdate) {
		return { bgColor: draftUpdatedBgColor, icon: <Edit sx={{ fontSize: 14 }} /> };
	}
	return {};
};

const ShiftBlockLabel: FC<{ shift: NurseInShift }> = ({ shift }) => {
	const draftVisuals = getDraftVisuals(shift);
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				textDecoration: draftVisuals.strikeThrough ? "line-through" : undefined,
				opacity: draftVisuals.opacity,
			}}
		>
			{draftVisuals.icon}
			<NurseInShiftMainText nurseInShift={shift} />
		</Box>
	);
};

export type DayViewGapsProps = {
	date: DateTime;
	nurses: NurseInShift[];
	selectedNurse?: Nurse | null;
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
};

export const DayViewGaps: FC<DayViewGapsProps> = ({ date, nurses, selectedNurse, selectedLocations, setSelectedLocations, availableLocations }) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { getNurseFormattedName, chosenDepartment, getWeekIndexByDate, getDayIndexByDate, hospitalData, isHospital } = useHospitalData();
	const { holidays } = usePageScheduleHolidays();
	const theme = useTheme();
	const { settings } = useUserSettingsContext();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	// The same predicate the removal dialog renders its replacement checkbox from.
	const removalOffersReplacementSearch = useRemovalOffersReplacementSearch(false);
	const unionByNurseId = useMemo(() => buildNurseUnionMap(hospitalData.nurses), [hospitalData.nurses]);
	const gapColor = chosenDepartment.statsMode === StatsMode.Ratio ? colorForMissingBlockHighlighted : colorForMissingBlock;
	const gapFontColor = chosenDepartment.statsMode === StatsMode.Ratio ? fontColorMissingBlockHighlighted : fontColorMissingBlock;
	const weekIndex = useMemo(() => getWeekIndexByDate(date), [date, getWeekIndexByDate]);
	const dayIndex = useMemo(() => getDayIndexByDate(date), [date, getDayIndexByDate]);
	// Hospital care metric (WLS): the day's average patient score across its shifts (Day + Night).
	// Shown in the location row where SNF/ALF show HPPD. Backend-computed `score`; we only average.
	const wls = useMemo<number | null>(() => {
		if (!isHospital) return null;
		const scores = (hospitalData.weeks[weekIndex]?.weeklyShifts ?? [])
			.map((shift) => shift.days[dayIndex]?.score)
			.filter((score): score is number => typeof score === "number");
		return scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null;
	}, [isHospital, hospitalData.weeks, weekIndex, dayIndex]);

	const {
		calculatedData,
		calculatedPostedRequests,
		calculatedReplaceRequests,
		selectedRoleFilters,
		selectedJobFilters,
		selectedShiftFilter,
		sortingState,
		setSortingState,
		sortingButtonRef,
		topPanelRef,
		handleShiftFilterChange,
		handleRoleChipChange,
		handleJobChipChange,
		handleLocationButtonClick,
		setSelectedRequestWithPending,
		selectedRequestWithPending,
		handleShiftRowClick,
		selectedShiftForNurseMenu,
		handleCloseNurseMenu,
		setRequestToShowBroadcastMessagePopup,
		requestToShowBroadcastMessagePopup,
		relevantRolesForBroadcast,
		recommendationForSelectedRequestBroadcast,
		groupedLocations,
		toggleGroupExpand,
		expandedGroups,
		sensors,
		handleDragStart,
		handleDragEnd,
		dayStartHour,
		handleGapClick,
		usePickupByLocation,
		gapPostShiftState,
		setGapPostShiftState,
		gapPostedCoverage,
		selectedContractSections,
		handleContractSectionsChange,
	} = useDayViewGapsState({
		date,
		nurses,
		selectedNurse,
		selectedLocations,
		availableLocations,
	});

	const dayStart = date.startOf("day").set({ hour: dayStartHour });

	// The ONE Remove quick action the call-off walkthrough spotlights — rows walked in the order the list below
	// renders them, collapsed groups skipped because their rows are unmounted. Re-read every minute: a shift drifts out
	// of (or into) the call-off window with no data change.
	const pickCallOffAnchorItem = useCallback(
		(now: DateTime) => {
			if (calculatedData === null) return null;
			const renderedLocationKeys = groupedLocations?.length
				? groupedLocations.filter((group) => expandedGroups.has(group.groupKey)).flatMap((group) => group.locationKeys)
				: Object.keys(calculatedData);
			return gapsCallOffAnchorItem({
				calculatedData,
				renderedLocationKeys,
				date,
				dayStartHour,
				offersReplacementSearch: removalOffersReplacementSearch,
				now,
			});
		},
		[calculatedData, groupedLocations, expandedGroups, date, dayStartHour, removalOffersReplacementSearch],
	);
	const callOffAnchorItem = useValueEveryMinute(pickCallOffAnchorItem);
	const isInPast = date.startOf("day") < DateTime.now().startOf("day");

	const handlePostedScrollPulse = useCallback(
		// Smooth-scroll to the posted-shift block for `requestId`
		(requestId: string) => {
			const el = document.getElementById(`${postShiftBlockDomIdLeading}-${requestId}`);
			if (!el) return;

			const flash = () => {
				const origBg = getComputedStyle(el).backgroundColor;
				const origColor = getComputedStyle(el).color;
				// Flash to the primary color, and swap the text to its contrast color in sync so the label stays
				// readable on the primary background regardless of the theme's primary.
				animate(
					el,
					{ backgroundColor: [origBg, theme.palette.primary.main, origBg], color: [origColor, theme.palette.primary.contrastText, origColor] },
					{ duration: POSTED_FLASH_DURATION_S, ease: "easeInOut" },
				);
				window.setTimeout(
					() => {
						el.style.backgroundColor = "";
						el.style.color = "";
					},
					POSTED_FLASH_DURATION_S * 1000 + 100,
				);
			};

			let scroller = el.parentElement;
			while (scroller && scroller !== document.body) {
				const overflowY = getComputedStyle(scroller).overflowY;
				if ((overflowY === "auto" || overflowY === "scroll") && scroller.scrollHeight - scroller.clientHeight > 8) break;
				scroller = scroller.parentElement;
			}
			const container: Element | null = scroller && scroller !== document.body ? scroller : document.scrollingElement;
			if (!container) {
				flash();
				return;
			}

			const from = container.scrollTop;
			const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
			const targetDelta = el.getBoundingClientRect().top - containerTop - (container.clientHeight - el.offsetHeight) / 2;
			const to = Math.max(0, Math.min(from + targetDelta, container.scrollHeight - container.clientHeight));
			if (Math.abs(to - from) < 2) {
				flash();
				return;
			}
			animate(from, to, {
				duration: POSTED_SCROLL_DURATION_S,
				ease: "easeInOut",
				onUpdate: (v) => {
					container.scrollTop = v;
				},
			}).then(flash);
		},
		[theme],
	);

	// Shared builder for a gap's main element — used by both the grouped and ungrouped render branches.
	// When pickup_by_location is OFF, a gap shows "Missing + (partially/fully posted X/Y)" from the
	// aggregate coverage; when ON, a matched gap renders <PostedGap> instead (postedRequestId set).
	const renderGapMainElement = useCallback(
		(
			gap: DayViewGap,
			currentRow: { locationName: string; roles: (NurseFullRole | "all")[] },
			clippedEndTimeForValue: DateTime,
			clippedNormalizedStart: DateTime,
		): RowElement => {
			const postedReqId = gap.value.postedRequestId;
			const coverage = !usePickupByLocation ? gapPostedCoverage?.get(getGapSignatureKey(gap.value.timeSlot, jobManagementEnabled)) : undefined;
			return {
				start: gap.value.timeSlot.start,
				end: clippedEndTimeForValue,
				normalizedStart: clippedNormalizedStart,
				normalizedEnd: gap.normalizedEndTime,
				key: `gap__${gap.value.gapIdx}`,
				mainShortText: postedReqId ? (
					<PostedGap requestId={postedReqId} />
				) : (
					<ClickableGap
						enabled={!isInPast}
						onClick={() =>
							handleGapClick(currentRow.locationName, gap.value.timeSlot.start, gap.value.timeSlot.end, currentRow.roles, gap.value.timeSlot.jobId)
						}
						coverage={coverage ?? null}
						onCoverageClick={handlePostedScrollPulse}
					/>
				),
				disabledHideTextOnHover: !postedReqId,
				bgColor: postedReqId ? colorForPostedShiftBlock : gapColor,
				color: postedReqId ? undefined : gapFontColor,
				gapIdx: gap.value.gapIdx,
			} as RowElement;
		},
		[usePickupByLocation, gapPostedCoverage, jobManagementEnabled, isInPast, handleGapClick, handlePostedScrollPulse, gapColor, gapFontColor],
	);

	const buildShiftTooltip = useCallback(
		(shiftValue: NurseInShift): string => {
			const lines: string[] = [shiftValue.name, `${t("role")}: ${shiftValue.role}`];
			if (shiftValue.jobName) lines.push(`${t("job")}: ${shiftValue.jobName}`);
			if (shiftValue.notes) lines.push(`Notes: ${shiftValue.notes}`);
			return lines.join("\n");
		},
		[t],
	);

	return (
		<DndContext autoScroll={{ threshold: { x: 0, y: 0.2 } }} onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
			<Box
				sx={{
					width: "100%",
					minHeight: "100%",
					p: DAY_VIEW_GAPS_PADDING,
					display: "flex",
					flexDirection: "column",
					gap: 2,
					justifyContent: "start",
				}}
			>
				<Box
					ref={topPanelRef}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						bgcolor: "white",
						p: 1,
						borderRadius: 1,
						boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
						}}
					>
						<SystemSortingButtonWithPopup
							ref={sortingButtonRef}
							popupPositions={["top", "right", "bottom", "left"]}
							sortingOptions={sortingOptions}
							initialState={sortingState}
							setSortState={setSortingState}
							defaultSortState={defaultSortStateDailyGapView}
							localStorageTableKey={dailyViewGapsTableSortingKey}
						/>
						<DayViewLocationFilter selectedLocations={selectedLocations} setSelectedLocations={setSelectedLocations} availableLocations={availableLocations} />
						<ContractSectionsDropdown
							dataTestId="day-view-gaps-contract-sections-dropdown"
							selectedSections={selectedContractSections}
							onChange={handleContractSectionsChange}
						/>
						<TimeFilterChips
							onSelectedFilterChange={handleShiftFilterChange}
							selectedFilters={[selectedShiftFilter]}
							allShiftsTimes={nurses.map((nurseInShift) => ({ start: nurseInShift.startTime, end: nurseInShift.endTime }))}
						/>
					</Box>
					<RoleOrJobSelector
						selectedRoles={selectedRoleFilters}
						onSelectedRolesChanged={handleRoleChipChange}
						minimumOneSelected={true}
						selectedJobIds={selectedJobFilters}
						onSelectedJobsChanged={handleJobChipChange}
						label={t("job")}
						formControlSx={{ width: "260px" }}
					/>
				</Box>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "start",
						bgcolor: "white",
						px: 1,
						borderRadius: 1,
						width: "100%",
						height: `calc(100% - ${topPanelRef.current?.clientHeight ?? 0}px - 50px)` /*Also include 50px of padding*/,
						overflow: "auto",
						zIndex: 0,
						gap: groupedLocations ? 0.25 : undefined,
						position: "relative",
						overflowX: "hidden",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "start",
							width: "100%",
							bgcolor: "white",
							gap: shiftGaps,
							px: rowPadding,
							position: "sticky",
							zIndex: 1,
							top: 0,
							py: 0.5,
							borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
						}}
					>
						<Box
							sx={{
								minWidth: locationAndRolesWidth,
							}}
						>
							<SystemButton
								buttonVariant={"borderless"}
								leadingIcon={<SwapVertIcon fontSize={"small"} />}
								sx={{
									...hourMarkerTypographySx,
									height: "fit-content",
									p: "4px",
								}}
								onClick={handleLocationButtonClick}
							>
								Location
							</SystemButton>
						</Box>
						<Box
							sx={{
								position: "relative",
								display: "flex",
								flex: 1,
								height: "100%",
							}}
						>
							{[...Array(24 / hourJumpBetweenMarkers).keys()].map((i) => {
								const hourDiff = i * hourJumpBetweenMarkers;
								const currentTime = dayStart.plus({ hours: hourDiff });
								return (
									<Box
										key={i}
										sx={{
											position: "absolute",
											left: `calc((100%) * ${hourDiff / 24})`,
											top: "50%",
											...hourMarkerTypographySx,
											color: markerColor,
											transform: "translateY(-50%)",
										}}
									>
										{currentTime.toFormat("hha")}
									</Box>
								);
							})}
						</Box>
					</Box>
					{calculatedPostedRequests != null && Object.keys(calculatedPostedRequests).length > 0 && (
						<Box
							sx={{
								width: "100%",
								height: "fit-content",
								display: "flex",
								flexDirection: "column",
								gap: rowGap,
							}}
						>
							{calculatedPostedRequests &&
								Object.keys(calculatedPostedRequests).map((currentRequestKey) => {
									const currentRequestRow = calculatedPostedRequests[currentRequestKey];
									return (
										<DailyRow
											key={currentRequestKey}
											rowKey={currentRequestKey}
											isMissingConfig={false}
											date={date}
											roles={currentRequestRow.roles}
											name={postedShiftString}
											locationJobId={currentRequestRow.jobId}
											disableBottomBorder={true}
											rowBgColor={colorForMissingBlock}
											rows={currentRequestRow.rows.map((slot) => ({
												start: slot.start,
												end: slot.end,
												elements: slot.requests.map((requestElement) => {
													const request = requestElement.value;
													const pendingStatus = request.message.request_data.filter((req) => req.status === RequestResponsesStatusOptions.PENDING);
													const amountNeededNurses = "needed_nurses" in request ? request.needed_nurses : 0;
													// DEFAULT: Keep the existing label when a job name is unavailable.
													const roleOrJobName =
														jobManagementEnabled && request.job_id
															? (chosenDepartment.jobs?.find((job) => job.id === request.job_id)?.displayName ?? "staff")
															: getGroupNameForRoles(request.message.roles, chosenDepartment);
													const isAgencyReplacement = request.request_type === NotificationType.ReplaceAgency;
													return {
														start: clampToDayStart(requestElement.normalizedStartTime, dayStart),
														end: requestElement.normalizedEndTime,
														key: request.id,
														domId: `${postShiftBlockDomIdLeading}-${request.id}`,
														mainShortText: `${amountNeededNurses} ${roleOrJobName} needed`,
														bgColor: colorForPostedShiftBlock,
														backgroundImage: isAgencyReplacement ? DAY_GAPS_POSTED.LOW_PRIORITY_HATCH : undefined,
														color: isAgencyReplacement ? DAY_GAPS_POSTED.LOW_PRIORITY_INK : undefined,
														fontWeight: isAgencyReplacement ? DAY_GAPS_POSTED.LOW_PRIORITY_WEIGHT : undefined,
														tooltipFullText: isAgencyReplacement ? t("day_view_gaps.replace_agency.tooltip") : undefined,
														accessibleLabel: isAgencyReplacement
															? t("day_view_gaps.replace_agency.accessible_label", {
																	start: request.message.shift_start.toFormat("h:mma"),
																	end: request.message.shift_end.toFormat("h:mma"),
																	role: roleOrJobName,
																	needed: t("gaps.requests.needed", { count: amountNeededNurses }),
																})
															: undefined,
														accessibleDescription: isAgencyReplacement ? t("day_view_gaps.replace_agency.tooltip") : undefined,
														shortTextTrailingNode: (isHovered, hideActionsWhenNotHovered) => (
															<RequestsLeadingContent
																isHovered={isHovered}
																request={request}
																setRequestToShowBroadcastMessagePopup={setRequestToShowBroadcastMessagePopup}
																hideActionsWhenNotHovered={hideActionsWhenNotHovered}
															/>
														),
														leadingBadge: request.location?.trim() ? { text: request.location.trim() } : undefined,
														badge: {
															text: pendingStatus.length === 0 ? "no responses" : `${pendingStatus.length.toString()} pending`,
															badgeSx: pendingStatus.length === 0 ? { opacity: 0.5 } : undefined,
															onBadgeClick:
																pendingStatus.length > 0
																	? (e) => {
																			e.stopPropagation();
																			trackEvent("daily_gaps_view_pending_requests_open", {
																				request_id: request.id,
																			});
																			setSelectedRequestWithPending(request);
																		}
																	: undefined,
														},
													} as const satisfies RowElement;
												}),
											}))}
										/>
									);
								})}
							<Divider color={"var(--inhouse-primary-light)"} sx={{ borderBottomWidth: "2px" }} />
						</Box>
					)}
					{calculatedReplaceRequests != null && Object.keys(calculatedReplaceRequests).length > 0 && (
						<Box
							sx={{
								width: "100%",
								height: "fit-content",
								display: "flex",
								flexDirection: "column",
								gap: rowGap,
							}}
						>
							{calculatedReplaceRequests &&
								Object.keys(calculatedReplaceRequests).map((currentRequestKey) => {
									const currentRequestRow = calculatedReplaceRequests[currentRequestKey];
									return (
										<DailyRow
											key={currentRequestKey}
											rowKey={currentRequestKey}
											isMissingConfig={false}
											date={date}
											roles={currentRequestRow.roles}
											name={replaceRequestString}
											locationJobId={currentRequestRow.jobId}
											disableBottomBorder={true}
											rowBgColor={colorForMissingBlock}
											rows={currentRequestRow.rows.map((slot) => ({
												start: slot.start,
												end: slot.end,
												elements: slot.requests.map((requestElement) => {
													const request = requestElement.value;
													const pendingStatus = request.message.request_data.filter((req) => req.status === RequestResponsesStatusOptions.PENDING);
													const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;
													const mainText = isReplaceRequest
														? `Replace ${getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id)}`
														: `${"needed_nurses" in request ? request.needed_nurses : 1} ${jobManagementEnabled && request.job_id ? (chosenDepartment.jobs?.find((j) => j.id === request.job_id)?.displayName ?? "staff") : getGroupNameForRoles(request.message.roles, chosenDepartment)} needed`;
													return {
														start: clampToDayStart(requestElement.normalizedStartTime, dayStart),
														end: requestElement.normalizedEndTime,
														key: request.id,
														mainShortText: mainText,
														bgColor: colorForReplaceRequestBlock,
														shortTextTrailingNode: (isHovered, hideActionsWhenNotHovered) => (
															<RequestsLeadingContent
																isHovered={isHovered}
																request={request}
																setRequestToShowBroadcastMessagePopup={setRequestToShowBroadcastMessagePopup}
																hideActionsWhenNotHovered={hideActionsWhenNotHovered}
															/>
														),
														leadingBadge: request.location?.trim() ? { text: request.location.trim() } : undefined,
														badge: {
															text: pendingStatus.length === 0 ? "no responses" : `${pendingStatus.length.toString()} pending`,
															badgeSx: pendingStatus.length === 0 ? { opacity: 0.5 } : undefined,
															onBadgeClick:
																pendingStatus.length > 0
																	? (e) => {
																			e.stopPropagation();
																			trackEvent("daily_gaps_view_pending_replace_requests_open", {
																				request_id: request.id,
																			});
																			setSelectedRequestWithPending(request);
																		}
																	: undefined,
														},
													};
												}),
											}))}
										/>
									);
								})}
							<Divider
								color={"var(--inhouse-primary-light)"}
								sx={{
									borderBottomWidth: "2px",
								}}
							/>
						</Box>
					)}
					{calculatedData === null
						? "loading..."
						: Object.keys(calculatedData).length === 0
							? "No locations found in department"
							: groupedLocations?.length
								? groupedLocations.map((group) => {
										const isCollapsed = !expandedGroups.has(group.groupKey);
										// group.groupKey is the location name in Auto mode. Pass every location
										// sharing that name so the subtext aggregates across same-named variants.
										const autoSubtext =
											settings.censusByLocation === LocationMode.Auto
												? getAutoGroupSubtext(chosenDepartment.locations.filter((l) => l.location === group.groupKey))
												: undefined;

										return (
											<Box
												key={group.groupKey}
												sx={{
													width: "100%",
													display: "flex",
													flexDirection: "column",
												}}
											>
												<CensusGroupHeaderContent
													toggleGroupExpand={() => toggleGroupExpand(group.groupKey)}
													name={group.groupName}
													subtext={autoSubtext}
													locationKey={group.groupKey}
													isCollapsed={isCollapsed}
													date={date}
													weekIndex={weekIndex}
													dayIndex={dayIndex}
													showActual={false}
													stats={[
														{ value: group.nursesCount, label: "staff" },
														{ value: group.censusCount, label: "census" },
														...(chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType === HppdSeparationType.None
															? [{ label: `HPPD: ${group.hppd.toFixed(2)}` }]
															: []),
														// Hospitals show WLS (workload score) where SNF/ALF show HPPD.
														...(isHospital && wls !== null ? [{ label: `WLS: ${wls.toFixed(1)}` }] : []),
													]}
													hppdBreakdown={
														chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType !== HppdSeparationType.None
															? computeHppdBreakdown(
																	group.nurses,
																	group.censusCount,
																	chosenDepartment.department_id,
																	chosenDepartment.breakRulesConfig || [],
																	settings.hppdSeparationType,
																	chosenDepartment,
																	// Positional gap: the two `undefined`s hold the existing defaults; only the union map is new.
																	undefined,
																	undefined,
																	unionByNurseId,
																)
															: undefined
													}
													totalHppd={
														chosenDepartment.statsMode === StatsMode.PPD_HPPD && settings.hppdSeparationType !== HppdSeparationType.None
															? group.hppd
															: undefined
													}
													roleRatios={chosenDepartment.statsMode === StatsMode.Ratio && !jobManagementEnabled ? buildRoleRatios(group) : undefined}
													jobRatios={
														chosenDepartment.statsMode === StatsMode.Ratio && jobManagementEnabled ? buildJobRatios(group, chosenDepartment.jobs) : undefined
													}
												/>
												<Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
													<Box
														sx={{
															display: "flex",
															flexDirection: "column",
														}}
													>
														{group.locationKeys.map((currentKey) => {
															const currentRow = calculatedData[currentKey];
															return (
																<DailyRow
																	key={currentKey}
																	rowKey={currentKey}
																	isMissingConfig={currentRow.isMissingConfig}
																	date={date}
																	roles={currentRow.roles}
																	name={currentRow.locationName}
																	locationKey={currentKey}
																	locationJobId={currentRow.jobId}
																	rows={currentRow.rows.map((slot) => {
																		const clippedEndTime = slot.end.startOf("hour");
																		return {
																			start: slot.start,
																			end: clippedEndTime,
																			elements: slot.shiftsAndGaps.map((value) => {
																				const clippedEndTimeForValue =
																					value.specificType === "shift" ? value.value.endTime.startOf("hour") : value.value.timeSlot.end.startOf("hour");
																				const realStartEndTimes =
																					value.specificType === "shift"
																						? {
																								realStart: value.value.startTime,
																								realEnd: value.value.endTime,
																							}
																						: {
																								realStart: value.value.timeSlot.start,
																								realEnd: value.value.timeSlot.end,
																							};
																				const clippedNormalizedStart = clampToDayStart(value.normalizedStartTime, dayStart);
																				if (value.specificType === "shift") {
																					const shiftValue = value.value;
																					return {
																						start: shiftValue.startTime,
																						end: clippedEndTimeForValue,
																						realStartEndTimes,
																						normalizedStart: clippedNormalizedStart,
																						normalizedEnd: value.normalizedEndTime,
																						key: `${shiftValue.id}__${shiftValue.nurseId}_${shiftValue.location || "no-loc"}_${shiftValue.startTime.valueOf()}_${shiftValue.endTime.valueOf()}_${shiftValue.isBeingMoved ? "moving" : "static"}_${shiftValue.isOptimisticLoading ? "optimistic" : "loaded"}`,
																						mainShortText: <ShiftBlockLabel shift={shiftValue} />,
																						tooltipFullText: buildShiftTooltip(shiftValue),
																						bgColor: getDraftVisuals(shiftValue).bgColor ?? roleColor(shiftValue.role).fill,
																						shortTextTrailingNode: (isHovered, hideActionsWhenNotHovered) => (
																							<NurseInShiftTrailingNode
																								nurseInShift={shiftValue}
																								isHovered={isHovered}
																								onQuickActionSelected={(action, anchorEl) => handleShiftRowClick(shiftValue, anchorEl, action)}
																								hideActionsWhenNotHovered={hideActionsWhenNotHovered}
																								isCallOffWalkthroughAnchor={value === callOffAnchorItem}
																								shiftHoliday={holidayForShift(shiftValue, holidays)}
																							/>
																						),
																						onRowClick: (e) => {
																							e.stopPropagation();
																							handleShiftRowClick(shiftValue, e.currentTarget);
																						},
																						nurseInShift: shiftValue,
																						isOptimisticLoading: shiftValue.isOptimisticLoading,
																						isBeingMoved: shiftValue.isBeingMoved,
																					};
																				}
																				return renderGapMainElement(value, currentRow, clippedEndTimeForValue, clippedNormalizedStart);
																			}),
																		};
																	})}
																/>
															);
														})}
													</Box>
												</Collapse>
											</Box>
										);
									})
								: Object.keys(calculatedData).map((currentKey) => {
										const currentRow = calculatedData[currentKey];
										return (
											<DailyRow
												key={currentKey}
												rowKey={currentKey}
												isMissingConfig={currentRow.isMissingConfig}
												date={date}
												roles={currentRow.roles}
												name={currentRow.locationName}
												locationKey={currentKey}
												locationJobId={currentRow.jobId}
												rows={currentRow.rows.map((slot) => {
													const clippedEndTime = slot.end.startOf("hour");
													return {
														start: slot.start,
														end: clippedEndTime,
														elements: slot.shiftsAndGaps.map((value) => {
															const clippedEndTimeForValue =
																value.specificType === "shift" ? value.value.endTime.startOf("hour") : value.value.timeSlot.end.startOf("hour");
															const realStartEndTimes =
																value.specificType === "shift"
																	? {
																			realStart: value.value.startTime,
																			realEnd: value.value.endTime,
																		}
																	: {
																			realStart: value.value.timeSlot.start,
																			realEnd: value.value.timeSlot.end,
																		};
															const clippedNormalizedStart = clampToDayStart(value.normalizedStartTime, dayStart);
															if (value.specificType === "shift") {
																const shiftValue = value.value;
																return {
																	start: shiftValue.startTime,
																	end: clippedEndTimeForValue,
																	realStartEndTimes,
																	normalizedStart: clippedNormalizedStart,
																	normalizedEnd: value.normalizedEndTime,
																	key: `${shiftValue.id}__${shiftValue.nurseId}_${shiftValue.location || "no-loc"}_${shiftValue.startTime.valueOf()}_${shiftValue.endTime.valueOf()}_${shiftValue.isBeingMoved ? "moving" : "static"}_${shiftValue.isOptimisticLoading ? "optimistic" : "loaded"}`,
																	mainShortText: <ShiftBlockLabel shift={shiftValue} />,
																	tooltipFullText: buildShiftTooltip(shiftValue),
																	bgColor: getDraftVisuals(shiftValue).bgColor ?? roleColor(shiftValue.role).fill,
																	shortTextTrailingNode: (isHovered, hideActionsWhenNotHovered) => (
																		<NurseInShiftTrailingNode
																			nurseInShift={shiftValue}
																			isHovered={isHovered}
																			onQuickActionSelected={(action, anchorEl) => handleShiftRowClick(shiftValue, anchorEl, action)}
																			hideActionsWhenNotHovered={hideActionsWhenNotHovered}
																			isCallOffWalkthroughAnchor={value === callOffAnchorItem}
																			shiftHoliday={holidayForShift(shiftValue, holidays)}
																		/>
																	),
																	onRowClick: (e) => {
																		e.stopPropagation();
																		handleShiftRowClick(shiftValue, e.currentTarget);
																	},
																	nurseInShift: shiftValue,
																	isOptimisticLoading: shiftValue.isOptimisticLoading,
																	isBeingMoved: shiftValue.isBeingMoved,
																};
															}
															return renderGapMainElement(value, currentRow, clippedEndTimeForValue, clippedNormalizedStart);
														}),
													};
												})}
											/>
										);
									})}
					<PtoGapViewSection date={date} selectedNurse={selectedNurse} />
				</Box>
				{!!selectedRequestWithPending && (
					<NormalizedRequestApprovalDialog
						open={!!selectedRequestWithPending}
						request={selectedRequestWithPending}
						onClose={() => {
							trackEvent("daily_gaps_view_pending_requests_close", {
								request_id: selectedRequestWithPending.id,
							});
							setSelectedRequestWithPending(null);
						}}
					/>
				)}
				{selectedShiftForNurseMenu && (
					<NormalizedNurseMenu
						nurseInShift={selectedShiftForNurseMenu.shift}
						menuAnchorElement={selectedShiftForNurseMenu.anchorEl}
						currentQuickAction={selectedShiftForNurseMenu.quickAction}
						source={ChangesSource.Staffing}
						onClose={handleCloseNurseMenu}
						allowEditLocationAndHours={true}
						showActual={false}
					/>
				)}
				{recommendationForSelectedRequestBroadcast && (
					<NewBroadcastMessageModal
						initialSmsMessage={getShiftPickupMessage(
							recommendationForSelectedRequestBroadcast.shiftTimes?.date,
							recommendationForSelectedRequestBroadcast.shiftTimes?.start,
							recommendationForSelectedRequestBroadcast.shiftTimes?.end,
						)}
						addAppLink={true}
						defaultWorkingFilter={MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK}
						nurseIdsToIgnore={recommendationForSelectedRequestBroadcast.suggestions.flatMap((s) =>
							s.otherNurseId ? [s.nurseId, s.otherNurseId] : [s.nurseId],
						)}
						initialRolesToSelect={relevantRolesForBroadcast}
						jobId={requestToShowBroadcastMessagePopup?.job_id}
						onClose={() => setRequestToShowBroadcastMessagePopup(null)}
						initialFilters={
							recommendationForSelectedRequestBroadcast.shiftTimes
								? [
										{
											workFilter: MessageWorkingFilterEnum.STAFF_THAT_DOES_NOT_WORK,
											dateFilter: recommendationForSelectedRequestBroadcast.shiftTimes.date,
											timeFilter: {
												start: recommendationForSelectedRequestBroadcast.shiftTimes.start,
												end: recommendationForSelectedRequestBroadcast.shiftTimes.end,
											},
										},
									]
								: undefined
						}
					/>
				)}
				{gapPostShiftState && (
					/*Gap clicked rendering result:*/
					<StaffingChanges
						setIsAdd={() => setGapPostShiftState(null)}
						dayNo={gapPostShiftState.dayNo}
						shiftNo={0}
						rolesToDisplay={gapPostShiftState.roles}
						onClose={() => setGapPostShiftState(null)}
						defaultTab="create-shift"
						defaultRoom={gapPostShiftState.locationName}
						defaultJobId={gapPostShiftState.jobId ?? undefined}
						// A gap click always posts its one prefilled shift — hide recommendations + auto-check the prefilled
						hideRecommendations={true}
						lockedTimes={{
							startTime: gapPostShiftState.startTime,
							endTime: gapPostShiftState.endTime,
						}}
					/>
				)}
			</Box>
		</DndContext>
	);
};
